/**
 * Set-first-time-password landing page.
 *
 * Reached from the welcome email — the URL is
 * `https://app.harmoniq.aria-is.com/auth/set-password/:token`. On
 * mount the component validates the token via `passwordService`.
 *  - status "valid"   → render the create-password form
 *  - status "expired" → render the link-expired contact-support card
 *
 * The validation + set-password endpoints are stubs for now (no BFF
 * route yet) — see `passwordService` for the swap-in points.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Check,
  CheckCircle2,
  Copy,
  Eye,
  EyeOff,
  Mail,
} from "lucide-react";
// zxcvbn-ts is dynamically imported via ``loadZxcvbn()`` below. With
// static imports the dictionaries (~1.68 MB of `lastnames.json`,
// `passwords.json`, `wikipedia.json`, `commonWords.json`, …) get
// inlined into the app's main chunk — 79% of its weight, paid by
// every visitor including the vast majority who never see this page
// (set-password is a token-link landing surface from a one-off
// welcome / reset email). The dynamic import emits separate Rollup
// chunks that only download when this component decides it needs
// them. See B0 in docs/redux-toolkit-todo.md for the B1 audit
// numbers that motivated this change.

import passwordService from "../../services/passwordService";
import { ROUTES } from "../../constants/routes";
import { useSelector } from "react-redux";
import { selectIsAuthenticated } from "../../store/slices/authSlice";
import { appToast } from "../../toast.js";

import "./SetPassword.css";

const SUPPORT_EMAIL = "harmoniq-support@aria-is.com";

// Lazy-load zxcvbn-ts and its English dictionary. Returns a promise
// that resolves to the configured `zxcvbn` scoring function. Promise
// is cached at module scope so concurrent callers (the mount-time
// preload effect, the submit-time guard, a fast typist's first
// keystroke) all share one in-flight download — the second await
// returns the same resolved fn synchronously after the first
// completes. The dictionaries (~30k common passwords, English
// wordlists, keyboard layouts, l33t-table) are configured once
// inside the .then so the configuration doesn't re-run on every
// load() call.
let zxcvbnLoader = null;
function loadZxcvbn() {
  if (!zxcvbnLoader) {
    zxcvbnLoader = Promise.all([
      import("@zxcvbn-ts/core"),
      import("@zxcvbn-ts/language-common"),
      import("@zxcvbn-ts/language-en"),
    ]).then(([core, common, en]) => {
      core.zxcvbnOptions.setOptions({
        translations: en.translations,
        graphs: common.adjacencyGraphs,
        dictionary: {
          ...common.dictionary,
          ...en.dictionary,
        },
      });
      return core.zxcvbn;
    });
  }
  return zxcvbnLoader;
}

// Hard policy rules — every check must pass before submit is
// enabled, regardless of zxcvbn's score. Kept local to this page;
// SignIn / SignUp still use the looser shared `validatePassword`
// (≥6 chars).
const PASSWORD_RULES = [
  {
    id: "len",
    label: "At least 8 characters",
    short: "8+ characters",
    test: (v) => v.length >= 8,
  },
  {
    id: "upper",
    label: "An uppercase letter (A–Z)",
    short: "Uppercase",
    test: (v) => /[A-Z]/.test(v),
  },
  {
    id: "lower",
    label: "A lowercase letter (a–z)",
    short: "Lowercase",
    test: (v) => /[a-z]/.test(v),
  },
  {
    id: "digit",
    label: "A number (0–9)",
    short: "Number",
    test: (v) => /\d/.test(v),
  },
  {
    id: "symbol",
    label: "A special character (!@#$…)",
    short: "Symbol",
    test: (v) => /[^A-Za-z0-9]/.test(v),
  },
];

// Minimum zxcvbn score the user must clear to submit. zxcvbn
// returns 0–4 (4 = "very strong"); 3 ("strong") is the typical
// "okay to accept" threshold and rejects common-password
// substitutions like ``Password1!`` that pass our regex rules.
const MIN_ZXCVBN_SCORE = 2;

function evaluateRules(value) {
  return PASSWORD_RULES.map((rule) => ({ ...rule, ok: rule.test(value) }));
}

// Map zxcvbn's 0–4 score onto our 5-segment meter. Empty input
// keeps the meter blank; everything else fills (score + 1)
// segments so even a "very weak" entry shows a single red bar.
function meterSegmentsFromScore(score) {
  if (score == null) return 0;
  return Math.min(5, score + 1);
}

const STRENGTH_LABELS = ["Very weak", "Weak", "Fair", "Good", "Strong"];

export default function SetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  // "validating" → "valid" | "expired" | "success" | "error"
  const [phase, setPhase] = useState("validating");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Holds the resolved `zxcvbn` function once the dynamic import
  // lands. Stored as React state (not a ref) so the strength useMemo
  // below re-runs the moment the loader finishes, painting the meter
  // without waiting for the next keystroke. `useState(() => fn)`
  // updater form is required because React would otherwise *call* a
  // function value and store its return.
  const [zxcvbnFn, setZxcvbnFn] = useState(null);

  // StrictMode double-mount guard — same pattern as VerifyEmail.
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    // Already-signed-in users have no business on the set-password
    // landing page — bounce them to home with a toast so the
    // welcome-email link doesn't accidentally trigger a fresh
    // password setup over an active session.
    if (isAuthenticated) {
      calledRef.current = true;
      appToast.info("You're already signed in.");
      navigate(ROUTES.HOME, { replace: true });
      return;
    }
    calledRef.current = true;

    (async () => {
      try {
        const res = await passwordService.validateResetToken(token);
        // "used" / "valid" pass through verbatim; anything else
        // collapses to "expired" so unknown statuses still route to
        // the contact-support card rather than the form.
        const next = res?.status;
        if (next === "valid" || next === "used") setPhase(next);
        else setPhase("expired");
      } catch {
        setPhase("error");
      }
    })();
  }, [token, isAuthenticated, navigate]);

  // Preload zxcvbn the moment we know the user is going to see the
  // form. Skipped during "validating" / "expired" / "error" / "used"
  // / "success" phases because the form isn't rendered then — no
  // point spending 1.7 MB to fetch dictionaries the user will never
  // see strength meter for. The cancellation flag protects against
  // a fast unmount (user navigates away during the fetch) trying to
  // setState on an unmounted component.
  useEffect(() => {
    if (phase !== "valid") return undefined;
    let cancelled = false;
    loadZxcvbn().then((fn) => {
      if (!cancelled) setZxcvbnFn(() => fn);
    });
    return () => {
      cancelled = true;
    };
  }, [phase]);

  const rules = useMemo(() => evaluateRules(password), [password]);
  const allRulesMet = rules.every((r) => r.ok);

  // zxcvbn does the heavy lifting — dictionary, common-password,
  // keyboard-pattern, and l33t-substitution checks all roll into a
  // single 0–4 score plus human-readable feedback. Re-run it only
  // when the password actually changes; the call is fast (<5ms on
  // typical inputs) but no need to repeat it on every render. Stays
  // null until the dynamic-imported dictionaries land — the strength
  // meter is conditionally rendered on `strength`, so it simply
  // doesn't appear until the loader resolves (typically <1 s after
  // mount, well before the user finishes typing 8+ characters).
  const strength = useMemo(() => {
    if (!password) return null;
    if (!zxcvbnFn) return null;
    const result = zxcvbnFn(password);
    return {
      score: result.score,
      warning: result.feedback?.warning || "",
      suggestions: result.feedback?.suggestions || [],
    };
  }, [password, zxcvbnFn]);
  const meterScore = strength?.score ?? null;
  const segments = meterSegmentsFromScore(meterScore);
  const strengthLabel = meterScore == null ? "" : STRENGTH_LABELS[meterScore];

  const handlePasswordChange = (value) => {
    setPassword(value);
    setErrors((prev) => ({
      ...prev,
      // Field-level error is only shown after a submit attempt — the
      // live checklist is the in-flight feedback channel. Clear it
      // once the user types again.
      password: undefined,
      confirm:
        confirm && value !== confirm ? "Passwords do not match" : undefined,
    }));
    if (submitError) setSubmitError("");
  };

  const handleConfirmChange = (value) => {
    setConfirm(value);
    setErrors((prev) => ({
      ...prev,
      confirm:
        value && value !== password ? "Passwords do not match" : undefined,
    }));
    if (submitError) setSubmitError("");
  };

  // Hard gates that disable the button — these are always visible to
  // the user (empty fields, mismatched confirm, failed regex rules
  // marked X in the live checklist), so disabling reads cleanly. The
  // zxcvbn "strong enough" check is intentionally NOT in here: when
  // every rule is green and confirm matches, the button stays
  // clickable and the click surfaces a precise "not strong enough"
  // error — otherwise users see all-green checks above a disabled
  // button and read it as a broken form.
  const canSubmit =
    !!password && !!confirm && password === confirm && allRulesMet;

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Ensure zxcvbn is loaded so the strength gate has a real score
    // (the closure-captured `strength` is null while the dictionary
    // chunks are still in flight). In practice the mount-time preload
    // resolves long before the user finishes typing 8+ characters;
    // this `await` is the paste-and-immediately-submit safety net. If
    // the load fails (offline), we let the regex rules govern and
    // skip the zxcvbn gate — better to accept a slightly weaker
    // password than to lock the user out entirely.
    let liveScore = null;
    let liveWarning = "";
    try {
      const fn = zxcvbnFn || (await loadZxcvbn());
      const result = fn(password);
      liveScore = result.score;
      liveWarning = result.feedback?.warning || "";
    } catch {
      /* offline-tolerant: gate falls back to regex rules only */
    }
    const isStrongEnough =
      liveScore == null ? true : liveScore >= MIN_ZXCVBN_SCORE;
    const passwordErr = !allRulesMet
      ? "Password doesn't meet all requirements"
      : !isStrongEnough
        ? liveWarning ||
          "Please choose a stronger password — try a longer phrase or less common words."
        : "";
    const confirmErr = password !== confirm ? "Passwords do not match" : "";
    if (passwordErr || confirmErr) {
      setErrors({
        password: passwordErr || undefined,
        confirm: confirmErr || undefined,
      });
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      await passwordService.setPassword({ token, password });
      setPhase("success");
    } catch (err) {
      // Axios errors carry the BE-formatted reason on
      // ``response.data.detail`` (FastAPI default) or
      // ``response.data.message``. Fall through to the generic
      // hint when neither is present (network drop, CORS, etc.).
      const beMessage =
        err?.response?.data?.detail || err?.response?.data?.message;
      setSubmitError(
        typeof err === "string"
          ? err
          : beMessage ||
              err?.message ||
              "Unable to set your password. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="set-password-shell">
      <header className="set-password-topbar">
        <div className="set-password-logo-wrap">
          <Link to="/" aria-label="Go to home">
            <img
              className="set-password-logo"
              src="/sidemenulogo/harmoniq.png"
              alt="harmoniq"
            />
          </Link>
          <span className="set-password-product-tag">
            <span>an</span>
            <img
              className="set-password-aria"
              src="/aria-logo.png"
              alt="aria intelligent solutions"
            />
            <span>product</span>
          </span>
        </div>
      </header>

      <main className="set-password-main">
        {phase === "validating" && <ValidatingCard />}
        {phase === "error" && (
          <NetworkErrorCard onRetry={() => window.location.reload()} />
        )}
        {phase === "expired" && <ExpiredCard />}
        {phase === "used" && (
          <UsedCard
            onSignIn={() => navigate(ROUTES.SIGN_IN, { replace: true })}
          />
        )}
        {phase === "success" && (
          <SuccessCard
            onContinue={() => navigate(ROUTES.SIGN_IN, { replace: true })}
          />
        )}
        {phase === "valid" && (
          <section className="set-password-card set-password-card--form">
            <img
              className="set-password-form-logo"
              src="/logo-icon.svg"
              alt="harmoniq"
            />
            <h1 className="set-password-form-title">
              Create your HarmonIQ password.
            </h1>
            <p className="set-password-form-sub">
              Choose a strong password to finish activating your account. You&apos;ll
              use it to sign in from now on.
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <div className="set-password-field">
                <label htmlFor="set-password-pw">New Password</label>
                <div className="set-password-input">
                  <input
                    id="set-password-pw"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter at least 8 characters"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    aria-describedby="set-password-rules"
                  />
                  <button
                    type="button"
                    className="set-password-eye"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    onClick={() => setShowPassword((p) => !p)}
                  >
                    {showPassword ? (
                      <EyeOff width="16" height="16" />
                    ) : (
                      <Eye width="16" height="16" />
                    )}
                  </button>
                </div>

                {/* Strength meter — five segments driven by zxcvbn's
                    0–4 score (so common passwords like "Password1!"
                    still light up red even though they pass every
                    regex rule). Hidden until the user starts typing.
                    A second line surfaces zxcvbn's plain-English
                    warning ("This is similar to a commonly used
                    password" etc.) when present so the user knows
                    *why* the score is what it is. */}
                {password && strength && (
                  <>
                    <div
                      className={`set-password-strength set-password-strength--score-${meterScore}`}
                      aria-live="polite"
                    >
                      <div
                        className="set-password-strength-bars"
                        aria-hidden="true"
                      >
                        {PASSWORD_RULES.map((_, i) => (
                          <span
                            key={i}
                            className={`set-password-strength-bar${i < segments ? " set-password-strength-bar--on" : ""}`}
                          />
                        ))}
                      </div>
                      <span className="set-password-strength-label">
                        {strengthLabel}
                      </span>
                    </div>
                    {/* {(strength.warning || strength.suggestions.length > 0) && (
                      <p className="set-password-strength-feedback">
                        {strength.warning || strength.suggestions[0]}
                      </p>
                    )} */}
                  </>
                )}

                {/* Requirement hint — a single compact row of chips.
                    Neutral grey as a guide before/while typing; each
                    chip turns green the moment its rule is satisfied,
                    so the user sees exactly what's still missing
                    without the bulky multi-row checklist. */}
                <p id="set-password-rules" className="set-password-hint">
                  {rules.map((rule) => (
                    <span
                      key={rule.id}
                      className={`set-password-hint-chip${rule.ok ? " set-password-hint-chip--ok" : ""}`}
                    >
                      {rule.short}
                    </span>
                  ))}
                </p>
              </div>

              <div className="set-password-field">
                <label htmlFor="set-password-confirm">Confirm Password</label>
                <div className="set-password-input">
                  <input
                    id="set-password-confirm"
                    type={showConfirm ? "text" : "password"}
                    placeholder="Re-enter your password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => handleConfirmChange(e.target.value)}
                  />
                  <button
                    type="button"
                    className="set-password-eye"
                    aria-label={showConfirm ? "Hide password" : "Show password"}
                    onClick={() => setShowConfirm((p) => !p)}
                  >
                    {showConfirm ? (
                      <EyeOff width="16" height="16" />
                    ) : (
                      <Eye width="16" height="16" />
                    )}
                  </button>
                </div>
              </div>

              {(submitError || errors.password || errors.confirm) && (
                <div className="set-password-error" role="alert">
                  <AlertCircle width="14" height="14" />
                  <span>
                    {submitError || errors.password || errors.confirm}
                  </span>
                </div>
              )}

              {/* Stay enabled even when the form is invalid — clicking
                  Create password runs handleSubmit which surfaces a
                  precise error (rules failing / not strong enough /
                  passwords don't match). Disabling the button while
                  the live checklist shows everything green but zxcvbn
                  flags the password as weak read as "the form is
                  broken" — the click-to-see-why path is clearer. */}
              <button
                type="submit"
                className="set-password-cta"
                disabled={submitting || !canSubmit}
              >
                <span>{submitting ? "Saving…" : "Sign in to Workspace"}</span>
                {!submitting && <ArrowRight width="16" height="18" />}
              </button>
            </form>
          </section>
        )}
      </main>
    </div>
  );
}

function ValidatingCard() {
  return (
    <section className="set-password-card set-password-card--status">
      <div className="set-password-status-spinner" aria-hidden="true" />
      <h1 className="set-password-status-title">Checking your link…</h1>
      <p className="set-password-status-sub">
        Just a moment while we verify the invitation.
      </p>
    </section>
  );
}

function NetworkErrorCard({ onRetry }) {
  return (
    <section className="set-password-card set-password-card--status">
      <div
        className="set-password-status-icon set-password-status-icon--muted"
        aria-hidden="true"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </div>
      <h1 className="set-password-status-title">
        We couldn&apos;t reach our servers.
      </h1>
      <p className="set-password-status-sub">
        Something timed out on our side. Try the link again, and if it keeps
        failing, contact{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>
      <button
        type="button"
        className="set-password-status-cta"
        onClick={onRetry}
      >
        <span>Try Again</span>
        <ArrowRight width="18" height="18" />
      </button>
    </section>
  );
}

// Email rendered as a one-click mailto link plus a copy-to-clipboard
// icon (swaps to a check for 1.5s on success). Replaces the old
// email-as-a-big-button treatment.
function CopyableEmail({ email }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — the mailto link still works */
    }
  };
  return (
    <div className="set-password-email">
      <Mail
        className="set-password-email-icon"
        width="16"
        height="16"
        strokeWidth={2}
        aria-hidden="true"
      />
      <a
        href={`mailto:${email}?subject=${encodeURIComponent(
          "Request new HarmonIQ activation link",
        )}`}
        className="set-password-email-link"
      >
        {email}
      </a>
      <button
        type="button"
        className="set-password-email-copy"
        aria-label={copied ? "Email copied" : "Copy email address"}
        onClick={handleCopy}
      >
        {copied ? (
          <Check width="14" height="14" strokeWidth={2.5} />
        ) : (
          <Copy width="14" height="14" strokeWidth={2} />
        )}
      </button>
    </div>
  );
}

function ExpiredCard() {
  return (
    <section className="set-password-card set-password-card--expired">
      <div className="set-password-expired-icon" aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </div>
      <h1 className="set-password-expired-title">This invitation has expired.</h1>
      <p className="set-password-expired-sub">
        For your security account setup links are valid for 24 hours. Reach out
        to our team and we&apos;ll send you a fresh link.
      </p>
      <CopyableEmail email={SUPPORT_EMAIL} />
      {/* <p className="set-password-expired-foot">
        Don&apos;t have an account?{" "}
        <Link to={ROUTES.SIGN_UP} className="set-password-expired-link">
          Sign Up
        </Link>
      </p> */}
    </section>
  );
}

function UsedCard({ onSignIn }) {
  return (
    <section className="set-password-card set-password-card--status">
      <div className="set-password-status-icon" aria-hidden="true">
        <CheckCircle2 width="46" height="46" strokeWidth={1.6} />
      </div>
      <h1 className="set-password-status-title">
        You&apos;ve already set your password.
      </h1>
      <p className="set-password-status-sub">
        This setup link has already been used to create your password. Sign in
        with the credentials you chose to continue.
      </p>
      <button
        type="button"
        className="set-password-status-cta"
        onClick={onSignIn}
      >
        <span>Sign In</span>
        <ArrowRight width="18" height="18" />
      </button>
      <p className="set-password-status-foot">
        Forgot your password?{" "}
        <a
          href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
            "Reset HarmonIQ password",
          )}`}
          className="set-password-status-link"
        >
          Contact {SUPPORT_EMAIL}
        </a>
      </p>
    </section>
  );
}

function SuccessCard({ onContinue }) {
  return (
    <section className="set-password-card set-password-card--success">
      <div className="set-password-success-icon" aria-hidden="true">
        <CheckCircle2 width="46" height="46" strokeWidth={1.6} />
      </div>
      <h1 className="set-password-success-title">Your password is set.</h1>
      <p className="set-password-success-sub">
        You&apos;re all set. Sign in to start using HarmonIQ.
      </p>
      <button
        type="button"
        className="set-password-success-cta"
        onClick={onContinue}
      >
        <span>Continue to Sign In</span>
        <ArrowRight width="18" height="18" />
      </button>
    </section>
  );
}
